import {
    AppIcon,
    BellIcon,
    ButtonCodeIcon,
    CardAltIcon,
    ChipIcon,
    CircleDollarIcon,
    KioskIcon,
    LockIcon,
    SettingsIcon,
    ShieldIcon,
    TranslationIcon,
    UserIcon,
} from "@xola/icons";
import { Breadcrumb, HeaderToolbar } from "@xola/ui-kit";
import clsx from "clsx";
import { isUndefined } from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Permission, PurchaseRule, XolaMobileApp } from "types";
import { SidebarTabs } from "../components/Common/SidebarTabs";
import { ColumnLayout } from "../components/Layout/ColumnLayout";
import { DelegateList } from "../components/Settings/AccessManagement/DelegateList";
import { AppStore } from "../components/Settings/App/AppStore";
import { ButtonCode } from "../components/Settings/ButtonCode/ButtonCode";
import { CompanyProfileForm } from "../components/Settings/CompanyProfileForm";
import { GratuitySettings } from "../components/Settings/GratuitySettings";
import { GuideProfile } from "../components/Settings/GuideProfile";
import { HardwareSettings } from "../components/Settings/Hardware/HardwareSettings";
import { KioskSettings } from "../components/Settings/Kiosk/KioskSettings";
import { ProfileInfoForm } from "../components/Settings/MyProfile/ProfileInfoForm";
import { NotificationPreferences } from "../components/Settings/Notifications/NotificationPreferences";
import { PaymentSettingsTabs } from "../components/Settings/Payments/PaymentSettingsTabs";
import { Preferences } from "../components/Settings/Preferences/Preferences";
import { TabletAppSettings } from "../components/Settings/TabletAppSettings";
import { EditTaxAndFee } from "../components/Settings/TaxesAndFees/EditTaxAndFee";
import { TaxesAndFeesSettings } from "../components/Settings/TaxesAndFees/TaxAndFeesSettings";
import { useAuthorization } from "../hooks/useAuthorization";
import { useResource } from "../hooks/useResource";
import { Page } from "./Page";
import { useXolaMobileApps } from "@/components/MobileApps/XolaMobileAppsContext";
import { FraudFighter } from "@/components/Settings/FraudFighter/FraudFighter";
import { LanguageSettings } from "@/components/Settings/Languages/LanguageSettings";
import { config } from "@/config";
import { useSeller } from "@/hooks/useAccount";

export const SettingsPage = () => {
    const history = useHistory();
    const seller = useSeller();
    const { isGranted } = useAuthorization();
    const { section } = useParams<{ section?: string }>();
    const { action } = useParams<{ action?: string }>();
    const { xolaMobileApp } = useXolaMobileApps();

    const { purchaseRuleId, purchaseRuleLinkId } = useParams<{ purchaseRuleId?: ID; purchaseRuleLinkId?: ID }>();
    const { data: purchaseRule } = useResource<PurchaseRule>(["purchaseRules", purchaseRuleId]);

    const hasAccessCompanyProfile = isGranted(Permission.COMPANY_BASIC_INFO_VIEW);

    const hasHardwarePermissions =
        isGranted(Permission.PAYMENT_TERMINAL_VIEW) ||
        isGranted(Permission.RECEIPT_PRINTING_VIEW) ||
        isGranted(Permission.TICKET_PRINTING_VIEW);

    const shouldShowAccountOrMyLanguage = hasAccessCompanyProfile || seller.preferences.language.multiple;
    const hasLanguagePermissions =
        isGranted(Permission.MULTI_LANGUAGE_SUPPORT_VIEW) || (config.localize.enabled && shouldShowAccountOrMyLanguage);

    const tabs = [
        {
            section: "company",
            label: "Company Profile",
            description: "Basic information about your company",
            icon: <UserIcon />,
            content: <CompanyProfileForm />,
            permission: isGranted(Permission.COMPANY_PROFILE_VIEW),
        },
        {
            section: "profile",
            label: "My Profile",
            description: "Manage your Xola Information",
            icon: <UserIcon />,
            content: <ProfileInfoForm />,
        },
        {
            section: "guideProfile",
            label: "Guide Profile",
            description: "Manage your schedule and customer-facing bio",
            icon: <UserIcon />,
            content: <GuideProfile />,
            permission: isGranted(Permission.GUIDE_PROFILE_VIEW),
        },
        {
            section: "users",
            sectionAlias: "userManagement",
            label: "Users & Access",
            description: "Manage users and access levels",
            icon: <LockIcon />,
            permission: isGranted(Permission.ACCESS_MANAGER_VIEW),
            content: <DelegateList />,
        },
        {
            section: "languages",
            label: "Languages",
            description: "Manage the language you use Xola in and translations for your content",
            icon: <TranslationIcon />,
            content: <LanguageSettings />,
            permission: hasLanguagePermissions,
        },
        {
            section: "notifications",
            label: "Notifications",
            icon: <BellIcon />,
            description: "Manage email and SMS notifications to your customers and guide",
            content: <NotificationPreferences />,
            permission: isGranted(Permission.NOTIFICATIONS_SETTINGS_VIEW),
        },
        {
            section: "buttons",
            label: "Button Code",
            icon: <ButtonCodeIcon />,
            description: "Create and manage book now buttons for your website",
            content: <ButtonCode />,
            permission: isGranted(Permission.BUTTON_CODE_VIEW),
        },
        {
            section: "preferences",
            label: "Preferences",
            description: "Manage booking and gift settings",
            icon: <SettingsIcon />,
            content: <Preferences />,
            permission: isGranted(Permission.PREFERENCES_SETTINGS_VIEW),
        },
        {
            section: "fraudFighter",
            label: "Fraud Fighter",
            description: "Tools to help you combat fraudulent purchases",
            icon: <ShieldIcon />,
            content: <FraudFighter />,
            permission: isGranted(Permission.PREFERENCES_SETTINGS_VIEW),
        },
        {
            section: "gratuity",
            label: "Gratuity",
            description: "Manage your preferences for the Gratuity feature",
            icon: <CircleDollarIcon />,
            content: <GratuitySettings />,
            permission: isGranted(Permission.GRATUITY_SETTINGS_VIEW),
        },
        {
            section: "apps",
            label: "Apps",
            description: "Configure and manage your installed apps from the Xola App Store",
            icon: <AppIcon />,
            content: <AppStore height="100%" route="settings" />,
            permission: isGranted(Permission.APPS_SETTINGS_VIEW),
        },
        {
            section: "taxesAndFees",
            label: "Taxes and Fees",
            description: "Configure and manage your business' taxes and fees",
            icon: <CircleDollarIcon />,
            content:
                action === "create" || purchaseRule ? (
                    <EditTaxAndFee purchaseRule={purchaseRule ?? null} purchaseRuleLinkId={purchaseRuleLinkId} />
                ) : (
                    <TaxesAndFeesSettings />
                ),
            permission: isGranted(Permission.TAXES_AND_FEES_VIEW),
        },
        {
            section: "payments",
            label: "Payments",
            description: "Configure and manage payout details for payment processors",
            icon: <CardAltIcon />,
            content: <PaymentSettingsTabs />,
            permission: isGranted(Permission.PAYMENTS_SETTINGS_VIEW),
        },
        {
            section: "hardware",
            label: "Hardware",
            description: "Pair and manage POS and Credit Card devices",
            icon: <ChipIcon />,
            permission: hasHardwarePermissions,
            content: <HardwareSettings />,
        },
        {
            section: "kiosk",
            label: "Kiosk",
            description: "Configure settings for the Xola Kiosk app",
            icon: <KioskIcon />,
            permission: hasHardwarePermissions,
            content: <KioskSettings />,
        },
        {
            section: "tabletApp",
            label: "Tablet App",
            description: "Manage settings for the Xola Tablet app",
            icon: <ChipIcon />,
            permission: xolaMobileApp === XolaMobileApp.XolaTablet,
            content: <TabletAppSettings />,
        },
    ];
    const getSettingsIndex = (section = tabs[0].section) => {
        const tabIndex = tabs.findIndex((tab) => tab.section === section || tab.sectionAlias === section);
        return Math.max(0, tabIndex);
    };

    const initialSection = section ?? tabs[0].section;
    const initialActiveIndex = getSettingsIndex(initialSection);
    const [activeTabIndex, setActiveTabIndex] = useState(initialActiveIndex === -1 ? 0 : initialActiveIndex);

    useEffect(() => {
        if (section) {
            const newIndex = tabs.findIndex((tab) => tab.section === section || tab.sectionAlias === section);
            if (newIndex >= 0 && activeTabIndex !== newIndex) {
                setActiveTabIndex(newIndex);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    const breadcrumbs: Array<{ label: string; url?: string; tabIndex?: number }> = [
        { label: "Settings", url: "/settings", tabIndex: 0 },
        { label: tabs[activeTabIndex].label, tabIndex: activeTabIndex },
    ];

    const handleActiveTabChange = (index: number) => {
        history.push(`/settings/${tabs[index].section}`);
    };

    const handleBreadcrumbClick = (index: number, url?: string) => {
        if (index === 0 && !hasAccessCompanyProfile) {
            return;
        }

        if (url) {
            history.push(url);
        }

        handleActiveTabChange(index);
    };

    const title = tabs[activeTabIndex]?.label ?? "Global Settings";
    return (
        <Page title={title}>
            <ColumnLayout>
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb className="flex-grow border-r-0">
                        {breadcrumbs.map((breadcrumb) => (
                            <Breadcrumb.Item
                                key={breadcrumb.label}
                                onClick={() => handleBreadcrumbClick(breadcrumb.tabIndex ?? 0, breadcrumb.url)}
                            >
                                {breadcrumb.label}
                            </Breadcrumb.Item>
                        ))}
                    </HeaderToolbar.Breadcrumb>
                    <div className="shrink-0" id="header-toolbar-actions" />
                </HeaderToolbar>

                <ColumnLayout.Fill>
                    <SidebarTabs activeTab={activeTabIndex} onClick={handleActiveTabChange}>
                        {tabs.map((tab) => {
                            const isHidden = !isUndefined(tab.permission) && !tab.permission;
                            return (
                                <SidebarTabs.Tab
                                    key={tab.section}
                                    name={tab.label}
                                    description={tab.description}
                                    icon={tab.icon}
                                    className={clsx(isHidden && "hidden")}
                                >
                                    {tab.content}
                                </SidebarTabs.Tab>
                            );
                        })}
                    </SidebarTabs>
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};
